import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Components
import { Loader, BuildersDropdown } from '../../../pip/apps/components';

import AllApprovedApplications from './AllApprovedApplications';

// Helpers
import { filterProjects as filterApplications } from '../../../pip/apps/components/helpers';

// vars
const adminFilters = {
  status: 'Approved',
  producer: 0,
  year: ''
};

// Keep outside of function, or will get overwritten at re-render.
const _APPLICATIONSSCACHE = [];

const AdminPayments = () => {
  const [applications, setApplications] = useState([]);
  const [producers, setProducers] = useState([]);
  const [selectedApplicationIDs, setSelectedApplicationIDs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ ...adminFilters });
  const [mainNotice, setMainNotice] = useState({ type: '', message: '' });
  const [producer, setProducer] = useState({});
  const applicableYears = window.wlConfig.applicableYears;

  const parseAndsetProducers = () => {
    if (!_APPLICATIONSSCACHE.length) {
      setProducers([]);
      return;
    }

    const producers = _APPLICATIONSSCACHE.map(({ producer }) => producer);
    const reducedProducers = [];

    producers.filter((producer, index) => {
      const producerID = producer.ID;

      if (!reducedProducers.some((producer) => producer.ID === producerID)) {
        reducedProducers.push(producer);
      }
    });

    setProducers(reducedProducers);
  };

  const setLocalStorageFilter = (event) => {
    event.preventDefault();

    localStorage.setItem(
      'adminFilters',
      JSON.stringify({
        status: '',
        producer: filter.producer,
        state: ''
      })
    );

    window.location = event.currentTarget.href;
  };

  const handleFilterChange = (property, value) => {
    const updatedFilter = { ...filter };
    updatedFilter[property] = value;
    setFilter(updatedFilter);

    const producer = producers.find(
      (producer) => producer.ID == updatedFilter.producer
    );

    setProducer(producer);
  };

  const handleSelectApplication = (aid) => {
    if (!aid) return;

    let newArr = [...selectedApplicationIDs];

    // if id is already present in array, remove from array.
    if (newArr.includes(aid)) {
      newArr = selectedApplicationIDs.filter((id) => id !== aid);
    } else {
      // else add.
      newArr.push(aid);
    }

    setSelectedApplicationIDs(newArr);
  };

  const handleSelectAllApplications = (applications) => {
    if (!applications.length) return;

    const aids = applications.map((application) => application.aid);
    let newArr = [...selectedApplicationIDs];

    const allApplicationIDsPresent =
      newArr.length > 0 && aids.every((aid) => newArr.includes(aid));

    // If all Application IDs are present, bulk remove all of them.
    newArr = allApplicationIDsPresent ? [] : [...aids];

    setSelectedApplicationIDs(newArr);
  };

  const handleSendForPayment = (aids, actionType) => {
    if (!aids.length) return;
    let producerID = filter.producer;

    // If `producer` in filter obj is falsey, user has not selected a producer in dropdown menu, but has selected projects by clicking on cards (possibly by different producers).
    // Use different function instead.
    if (!producerID) {
      if (actionType === 'submit') {
        return handleSendForPaymentWithoutProducerInfo();
      }

      // We can't create a PDF for more than 1 producer. Check selected project IDs, and see what's up.
      if (actionType === 'download') {
        if (aids.length === 1) {
          const project = _APPLICATIONSSCACHE.find(
            ({ aid }) => aid === aids[0]
          );
          producerID = project.producer.ID;
        } else {
          const producerArray = [];

          aids.map((projectID) => {
            const project = _APPLICATIONSSCACHE.find(
              ({ aid }) => aid === projectID
            );

            if (!project) return false;

            producerArray.push(project.producer);
          });

          // If the selected projects are by more than 1 producer, bail.
          if (producerArray.length > 1) {
            return alert(
              'Can\'t generate a PDF for multiple producers. Make sure to only select projects by 1 producer (use the "-- Producer --" dropdown to filter by 1 producer).'
            );
          }
        }
      }
    }

    setLoading(true);
    setMainNotice({ type: '', message: '' });

    const formData = new FormData();
    formData.append('action', 'wl_fip_approve_for_payment');
    formData.append('type', actionType);
    formData.append('aids', aids);
    formData.append('pid', producerID);

    axios({
      method: 'post',
      url: window.wlPipConfig.ajaxUrl,
      data: formData
    })
      .then(({ data: { success, data } }) => {
        if (actionType === 'download') {
          const { pdfData } = data;
          const docTitle = 'FIP Payment PDF';
          const pdfObject = `<object width="100%" height="100%" data="data:application/pdf;base64,${pdfData}" type="application/pdf" class="internal"><embed src="data:application/pdf;base64,${pdfData}" type="application/pdf" /></object>`;

          let newPDFWindow = window.open('#', '_blank');
          newPDFWindow.document.write(`<html>
                <head>
                  <title>${docTitle}</title>
                </head>
                <body style="margin-top: 0px; margin-left: 0px; margin-right: 0px; margin-bottom: 0px;">
                  ${pdfObject}
                </body>
              </html>`);

          setMainNotice({
            type: success ? 'success' : 'error',
            message: data.message
          });
        } else if (actionType === 'submit') {
          const { updated_applications } = data;
          let noticeMessage = data.message;

          updated_applications.map(({ serialNumber, permalink }) => {
            noticeMessage += `<p class="u--margin-10">- <a href="${permalink}" class="u--color-black u--font-weight-medium u--font-size-md">${serialNumber}</a></p>`;
          });

          noticeMessage += '</ul>';

          setMainNotice({
            type: success ? 'success' : 'error',
            message: noticeMessage
          });

          _APPLICATIONSSCACHE.length = 0;
          _APPLICATIONSSCACHE.push(...data.applications);
          setApplications(filterApplications(_APPLICATIONSSCACHE, filter));
        }

        setLoading(false);
      })
      .catch((error) => {
        setMainNotice({
          type: 'error',
          message: error
        });

        setLoading(false);
      });
  };

  const handleSendForPaymentWithoutProducerInfo = () => {
    if (!selectedApplicationIDs.length) return;

    const applicationIDsByProducer = {};

    // Get application data for selected application IDs, and sort by producer.
    selectedApplicationIDs.map((aid) => {
      const application = _APPLICATIONSSCACHE.find(
        (application) => application.aid === aid
      );

      if (!application) return;

      const producerID = application.producer.ID.toString();

      if (producerID in applicationIDsByProducer) {
        applicationIDsByProducer[producerID].aids.push(aid);
      } else {
        const producerObject = {
          aids: [aid]
        };

        applicationIDsByProducer[producerID] = producerObject;
      }
    });

    setLoading(true);

    if (
      !confirm(
        `This will send ${selectedApplicationIDs.length} applications ${
          Object.keys(applicationIDsByProducer).length > 1
            ? `by ${
                Object.keys(applicationIDsByProducer).length
              } different producers `
            : ''
        }for approval, are you sure?`
      )
    ) {
      setLoading(false);
      return;
    }

    setMainNotice({ type: '', message: '' });

    const formData = new FormData();
    formData.append('action', 'wl_fip_approve_all_for_payment');
    formData.append('type', 'submitBulk');
    formData.append(
      'aids_by_producer',
      JSON.stringify(applicationIDsByProducer)
    );

    axios({
      method: 'post',
      url: window.wlPipConfig.ajaxUrl,
      data: formData
    })
      .then(({ data: { success, data } }) => {
        const { updated_applications } = data;
        let noticeMessage = data.message;

        updated_applications.map(({ serialNumber, permalink }) => {
          noticeMessage += `<p class="u--margin-10">- <a href="${permalink}" class="u--color-black u--font-weight-medium u--font-size-md">${serialNumber}</a></p>`;
        });

        noticeMessage += '</ul>';

        setSelectedApplicationIDs([]);

        _APPLICATIONSSCACHE.length = 0;
        _APPLICATIONSSCACHE.push(...data.applications);
        setApplications(filterApplications(_APPLICATIONSSCACHE, filter));

        setMainNotice({
          type: success ? 'success' : 'error',
          message: noticeMessage
        });
        setLoading(false);
      })
      .catch((error) => {
        setMainNotice({
          type: 'error',
          message: error
        });

        setLoading(false);
      });
  };

  const getApplications = () => {
    setLoading(true);

    const formData = new FormData();
    formData.append('action', 'wl_fip_admin_get_applications');
    formData.append('status', 'Approved');

    axios({
      method: 'post',
      url: window.wlPipConfig.ajaxUrl,
      data: formData
    })
      .then(({ data: { success, data } }) => {
        if (success) {
          _APPLICATIONSSCACHE.length = 0;
          _APPLICATIONSSCACHE.push(...data);
          setApplications(filterApplications(_APPLICATIONSSCACHE, filter));
        } else {
          setApplications([]);
          setMainNotice({
            type: success ? 'success' : 'error',
            message: data.message
          });
        }

        setSelectedApplicationIDs([]);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  // Update applications on filter change.
  useEffect(
    () =>
      setApplications(filterApplications(_APPLICATIONSSCACHE, filter, null)),
    [filter]
  );

  // Update Producers dropdown on applications update.
  useEffect(() => parseAndsetProducers(_APPLICATIONSSCACHE), [applications]);

  // Deselect any applications when a producer is selected.
  useEffect(() => setSelectedApplicationIDs([]), [producer]);

  // Init.
  useEffect(() => {
    if (_APPLICATIONSSCACHE.length) return;

    getApplications();
  }, []);

  if (!applications.length && loading) {
    return (
      <>
        <div className="grid">
          <div className="grid__col-xs-12">
            <h1 className="u--color-blue-dark u--margin-top-0">Payments</h1>
          </div>
        </div>
        <Loader />
      </>
    );
  }

  return (
    <>
      <div className="grid">
        <div className="grid__col-xs-12">
          <h1 className="u--color-blue-dark u--margin-top-0">Payments</h1>
        </div>
      </div>
      <div className="grid grid--gutter-vertical grid--row-stretch">
        {loading && <Loader />}

        <div className="grid__col-xs-12">
          <div className="form__select form__select--lg u--margin-bottom-0">
            <select
              className="form__input form__input--lg u--margin-bottom-20"
              disabled={loading === true}
              value={filter.year}
              onChange={(event) =>
                handleFilterChange('year', event.target.value)
              }
            >
              <option value="">-- All Years --</option>
              {applicableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div className="form__select form__select--lg u--margin-bottom-0">
            <BuildersDropdown
              builders={producers}
              disabled={loading === true}
              value={filter.producer}
              onChange={(event) =>
                handleFilterChange('producer', event.target.value)
              }
            />
          </div>
        </div>

        {applications.length > 0 && filter.producer != 0 ? (
          <div className="grid__col-xs-12 u--padding-bottom-0">
            <button
              className="btn btn--xs btn--gold"
              onClick={() => handleSelectAllApplications(applications)}
            >
              {selectedApplicationIDs.length === applications.length
                ? 'Deselect '
                : 'Select '}
              all applications
            </button>
            <ul style={{ listStyleType: 'none' }}>
              {applications.map(
                ({ equipmentType, serialNumber, aid }, index) => (
                  <li key={index}>
                    <label className="form__checkbox form__checkbox--sm u--margin-top-0">
                      <input
                        type="checkbox"
                        checked={
                          selectedApplicationIDs !== undefined &&
                          selectedApplicationIDs.includes(aid)
                        }
                        onChange={() => handleSelectApplication(aid)}
                      />{' '}
                      {`${equipmentType.replace(
                        /[A-Z]/g,
                        (letter) => ` ${letter.toLowerCase()}`
                      )} - ${serialNumber}`}
                    </label>
                  </li>
                )
              )}
            </ul>
          </div>
        ) : filter.producer != 0 ? (
          <div className="grid__col-xs-12 u--padding-bottom-0">
            <p className="u--font-size-md">
              Producer does not have any projects with an{' '}
              <strong>Approved</strong> status
              {filter.year ? ` for year ${filter.year}` : ''}.
              <br />
              <a
                href="/pfrp/dashboard/"
                onClick={(event) => setLocalStorageFilter(event)}
              >
                View all{' '}
                {producer.first_name && producer.last_name
                  ? `of ${producer.first_name} ${producer.last_name}'s `
                  : "producer's "}
                applications on the Admin Dashboard.
              </a>
            </p>
          </div>
        ) : (
          <AllApprovedApplications
            applications={applications}
            selectedApplicationIDs={selectedApplicationIDs}
            handleSelectApplication={handleSelectApplication}
          />
        )}
        {mainNotice.type && mainNotice.message && (
          <div className="grid__col-xs-12 u--padding-top-0">
            <div
              className={`notice notice--${mainNotice.type}`}
              role="alert"
              dangerouslySetInnerHTML={{ __html: mainNotice.message }}
            />
          </div>
        )}
        <div className="grid__col-xs-12 u--padding-bottom-0">
          <button
            className="btn btn--white btn--sm u--margin-right-10 u--margin-bottom-10"
            disabled={selectedApplicationIDs.length === 0 || loading === true}
            onClick={() =>
              handleSendForPayment(selectedApplicationIDs, 'download')
            }
            title="mererp"
          >
            <span className="btn__content">
              <span className="btn__text">Download payment information</span>
            </span>
          </button>
          <button
            className="btn btn--green btn--sm u--margin-bottom-10"
            disabled={selectedApplicationIDs.length === 0 || loading === true}
            onClick={() =>
              handleSendForPayment(selectedApplicationIDs, 'submit')
            }
          >
            <span className="btn__content">
              <span className="btn__text">
                Send selected{' '}
                {selectedApplicationIDs.length === 1
                  ? 'application'
                  : 'applications'}{' '}
                for payment to Bill.com
              </span>
            </span>
          </button>
        </div>
      </div>
    </>
  );
};

export default AdminPayments;
